import React, { useState, useEffect } from 'react';
import { Save, Check, CircleMinus, CircleCheck, AppWindow, Box, Code, Lock, Edit2 } from 'lucide-react';
import { ITApplication, ITComponentCategory, ITAppType } from './ITApplicationTypes';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../validation/ValidatedFields';
import { useTreeCycleGuard } from '../../validation/useTreeCycleGuard';

interface ITApplicationFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: Omit<ITApplication, 'id'>) => Promise<void>;
    initialData?: ITApplication | null;
    applications: ITApplication[];
    onToggleActive?: () => Promise<void>;
    canModify?: boolean;
}

const ITApplicationForm: React.FC<ITApplicationFormProps> = ({ isOpen, onClose, onSubmit, initialData, applications, onToggleActive, canModify = true }) => {
    const [formData, setFormData] = useState({
        name: '',
        category: 'APPLICATION' as ITComponentCategory,
        parentId: 'root',
        type: 'IN_HOUSE' as ITAppType,
        version: '',
        rollOutDate: '',
        purpose: '',
        vendor: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling]     = useState(false);
    // View-first edit: existing records open read-only ("Viewing") until the
    // user clicks Edit, which flips this to true ("Editing").
    const [isEditing, setIsEditing]   = useState(false);
    const appVh = useFieldValidation();
    const { forbiddenIds, cycleError } = useTreeCycleGuard(
        applications, initialData?.id ?? null, { idKey: 'id', parentKey: 'parentId' },
    );

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name,
                category: initialData.category,
                parentId: initialData.parentId || 'root',
                type: initialData.type || 'IN_HOUSE',
                version: initialData.version || '',
                rollOutDate: initialData.rollOutDate || '',
                purpose: initialData.purpose || '',
                vendor: initialData.vendor || ''
            });
        } else {
            setFormData({
                name: '',
                category: 'APPLICATION',
                parentId: 'root',
                type: 'IN_HOUSE',
                version: '',
                rollOutDate: new Date().toISOString().split('T')[0],
                purpose: '',
                vendor: ''
            });
        }
        setSubmitting(false);
        setToggling(false);
        setIsEditing(false);   // every open starts in read-only "Viewing" mode
        appVh.reset();
    }, [initialData, isOpen]);

    // Domain-level checks that must coexist with engine format errors.
    const runDomainChecks = (): boolean => {
        let ok = true;
        const name = formData.name.trim();
        if (
            name &&
            applications.some(
                (a) =>
                    a.id !== initialData?.id &&
                    a.name.trim().toLowerCase() === name.toLowerCase(),
            )
        ) {
            appVh.setError('name', 'Application with this name already exists');
            ok = false;
        }
        if (
            formData.category === 'APPLICATION' &&
            (formData.type === 'PURCHASED' || formData.type === 'LICENSED') &&
            !formData.vendor.trim()
        ) {
            appVh.setError('vendor', 'Vendor is required for Purchased/Licensed applications');
            ok = false;
        }
        const parentSelfId = formData.parentId && formData.parentId !== 'root' ? formData.parentId : null;
        const cycle = cycleError(parentSelfId, {
            self: 'An application cannot fall under itself.',
            descendant: 'Cannot fall under a descendant — that would create a hierarchy cycle.',
        });
        if (cycle) {
            appVh.setError('parentId', cycle);
            ok = false;
        }
        return ok;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const formatOk = appVh.validateAll([
            { name: 'name', code: 109, value: formData.name, required: true },
            { name: 'purpose', code: 110, value: formData.purpose },
            { name: 'version', code: 109, value: formData.version },
            { name: 'vendor', code: 109, value: formData.vendor },
            { name: 'rollOutDate', code: 305, value: formData.rollOutDate },
        ]);
        const domainOk = runDomainChecks();
        if (!formatOk || !domainOk) return;
        setSubmitting(true);
        try { await onSubmit(formData); } finally { setSubmitting(false); }
    };

    const handleToggle = async () => {
        if (!onToggleActive) return;
        setToggling(true);
        try { await onToggleActive(); } finally { setToggling(false); }
    };

    const isApproved = initialData?.workflow_state === 'approved';
    // Existing records are read-only until Edit is clicked; create is always editable.
    const fieldsDisabled = !!initialData && (!canModify || isApproved || !isEditing);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" data-testid="MOD_it_app_form">
            <Modal.Header>
                {initialData
                    ? <>
                        {isApproved ? 'Viewing (locked) ' : (canModify && isEditing) ? 'Editing ' : 'Viewing '}
                        {isApproved && <Lock className="lock-icon lock-icon--md" style={{ marginRight: 6 }} />}
                        "<span className="item-highlight">{initialData.name}</span>"
                      </>
                    : 'Create IT Application'}
            </Modal.Header>
            <Modal.Body>
                {isApproved && (
                    <div className="approved-banner" data-testid="BANNER_it_app_form_locked">
                        <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                    </div>
                )}
                <form
                    id="it-application-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-y-4"
                >
                    {/* System code (APP-N) — auto-generated on approve, read-only.
                        Only shown when editing; prefilled with the same code the list shows. */}
                    {initialData && (
                        <FormControl.Input
                            label="Code"
                            value={initialData.code || '—'}
                            disabled
                            data-testid="INP_it_app_code"
                        />
                    )}
                    {/* Name */}
                    <ValidatedInput
                        vh={appVh}
                        name="name"
                        code={109}
                        required
                        label="Name"
                        type="text"
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g. ERP System"
                        maxLength={200}
                        disabled={fieldsDisabled}
                        data-testid="INP_it_app_name"
                    />

                    {/* Category Selection */}
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { id: 'APPLICATION', label: 'App', icon: AppWindow, color: 'blue' },
                            { id: 'MODULE', label: 'Module', icon: Box, color: 'indigo' },
                            { id: 'FUNCTION', label: 'Function', icon: Code, color: 'violet' }
                        ].map(cat => (
                            <div
                                key={cat.id}
                                onClick={() => { if (!fieldsDisabled) setFormData({ ...formData, category: cat.id as ITComponentCategory }); }}
                                data-testid={`BTN_it_app_category_${cat.id}`}
                                className={`border rounded-lg p-2 flex items-center justify-center gap-2 transition-colors
                                    ${fieldsDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                                    ${formData.category === cat.id ? `bg-${cat.color}-50 border-${cat.color}-300` : 'hover:bg-slate-50 border-slate-200'}`}
                            >
                                <cat.icon size={20} className={formData.category === cat.id ? `text-${cat.color}-600` : 'text-slate-400'} />
                                <span className={`text-xs font-medium ${formData.category === cat.id ? `text-${cat.color}-700` : 'text-slate-600'}`}>{cat.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Parent Selection */}
                    <FormControl.SearchSelect
                        label="Falls Under"
                        value={formData.parentId}
                        onChange={(value) => {
                            setFormData({ ...formData, parentId: value ? String(value) : 'root' });
                            appVh.clearError('parentId');
                        }}
                        options={applications
                            .filter(a => !forbiddenIds.has(a.id)) // Prevent self + descendants (cycle-guard)
                            // Only approved + active applications are valid parents; keep the
                            // currently-selected parent visible even if it later went
                            // inactive/unapproved so the field still renders in edit mode.
                            .filter(a =>
                                a.id === formData.parentId ||
                                (a.is_active === 1 && a.workflow_state === 'approved')
                            )
                            .map(a => ({ value: a.id, label: `${a.name} (${a.category})` }))}
                        placeholder="-- None (Top Level) --"
                        clearable
                        disabled={fieldsDisabled}
                        error={appVh.errors.parentId}
                        data-testid="SEL_it_app_parent"
                    />

                    {/* Application Specific Fields */}
                    {formData.category === 'APPLICATION' && (
                        <div className="flex flex-col gap-y-4 pt-2 border-t border-slate-100">
                            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                <FormControl.SearchSelect
                                    label="Type"
                                    value={formData.type}
                                    onChange={(value) => setFormData({ ...formData, type: value as ITAppType })}
                                    options={[
                                        { value: 'IN_HOUSE', label: 'In-House' },
                                        { value: 'PURCHASED', label: 'Purchased' },
                                        { value: 'LICENSED', label: 'Licensed' },
                                    ]}
                                    disabled={fieldsDisabled}
                                    data-testid="SEL_it_app_type"
                                />
                                <ValidatedInput
                                    vh={appVh}
                                    name="version"
                                    code={109}
                                    label="Version"
                                    type="text"
                                    value={formData.version}
                                    onChange={e => setFormData({ ...formData, version: e.target.value })}
                                    placeholder="e.g. v2.4.1"
                                    disabled={fieldsDisabled}
                                    data-testid="INP_it_app_version"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                <ValidatedDatePicker
                                    vh={appVh}
                                    name="rollOutDate"
                                    code={305}
                                    label="Roll-out Date"
                                    value={formData.rollOutDate}
                                    onChange={e => setFormData({ ...formData, rollOutDate: e.target.value })}
                                    minDate={new Date()}
                                    disabled={fieldsDisabled}
                                    data-testid="INP_it_app_rollout_date"
                                />
                                {(formData.type === 'PURCHASED' || formData.type === 'LICENSED') && (
                                    <ValidatedInput
                                        vh={appVh}
                                        name="vendor"
                                        code={109}
                                        required
                                        label="Vendor"
                                        type="text"
                                        value={formData.vendor}
                                        onChange={e => setFormData({ ...formData, vendor: e.target.value })}
                                        placeholder="Vendor Name"
                                        disabled={fieldsDisabled}
                                        data-testid="INP_it_app_vendor"
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Purpose/Description */}
                    <ValidatedTextarea
                        vh={appVh}
                        name="purpose"
                        code={110}
                        label="Purpose / Description"
                        rows={3}
                        value={formData.purpose}
                        onChange={e => setFormData({ ...formData, purpose: e.target.value })}
                        placeholder="Brief description..."
                        disabled={fieldsDisabled}
                        data-testid="INP_it_app_purpose"
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className={initialData && onToggleActive && !isApproved ? 'app-modal-footer--split' : ''}>
                {/* Active/Inactive toggle — shown for existing non-approved records (viewing + editing) */}
                {initialData && onToggleActive && !isApproved && (
                    <Button
                        variant={initialData.is_active ? 'inactive-outline' : 'active-outline'}
                        icon={initialData.is_active ? CircleMinus : CircleCheck}
                        onClick={handleToggle}
                        disabled={toggling || submitting}
                        data-testid="BTN_it_app_toggle_active"
                    >
                        {toggling ? '...' : initialData.is_active ? 'Inactive' : 'Active'}
                    </Button>
                )}
                {/* View → Edit: existing record, not yet editing (brand color) */}
                {initialData && canModify && !isApproved && !isEditing && (
                    <Button
                        variant="fill"
                        icon={Edit2}
                        onClick={() => setIsEditing(true)}
                        data-testid="BTN_it_app_edit"
                    >
                        Edit
                    </Button>
                )}
                {/* Submit (create) or Update (existing + editing) */}
                {(!initialData || (canModify && !isApproved && isEditing)) && (
                    <Button
                        type="submit"
                        form="it-application-form"
                        variant={initialData ? 'update' : 'fill'}
                        icon={initialData ? Check : Save}
                        disabled={submitting || toggling}
                        data-testid="BTN_it_app_submit"
                    >
                        {submitting
                            ? (initialData ? 'Updating...' : 'Submitting...')
                            : (initialData ? 'Update' : 'Submit')}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ITApplicationForm;
